import * as React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@wayste/shadcn/components/dialog';
import { cn } from '@wayste/shadcn/lib/utils';
import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
    return (
        <CommandPrimitive
            data-slot="command"
            className={cn('bg-surface-tertiary text-text-primary flex h-full w-full flex-col overflow-hidden rounded-md', className)}
            {...props}
        />
    );
}

function CommandDialog({
    title = 'Command Palette',
    description = 'Search for a command to run...',
    children,
    className,
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof Dialog> & {
    title?: string;
    description?: string;
    className?: string;
    showCloseButton?: boolean;
}) {
    return (
        <Dialog {...props}>
            <DialogHeader className="sr-only">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogContent
                className={cn(
                    'overflow-hidden p-0 [&_[data-slot=dialog-close]]:right-6 [&_[data-slot=dialog-close]]:top-[22px]',
                    className,
                )}
                showCloseButton={showCloseButton}
            >
                <Command className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-item]_svg]:size-5">{children}</Command>
            </DialogContent>
        </Dialog>
    );
}

function CommandInput({
    className,
    containerClassName,
    ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & { containerClassName?: string }) {
    return (
        <div className={cn('p-3', containerClassName)}>
            <div
                data-slot="command-input-wrapper"
                className="border-border-strong focus-within:ring-border-focus flex items-center gap-2 rounded-md border px-3 py-2.5 focus-within:outline-none focus-within:ring-2"
            >
                <SearchIcon className="size-4 shrink-0 opacity-50" />
                <CommandPrimitive.Input
                    data-slot="command-input"
                    className={cn(
                        'placeholder:text-text-tertiary flex w-full rounded-md bg-transparent p-0 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
                        className,
                    )}
                    {...props}
                />
            </div>
        </div>
    );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
    return (
        <CommandPrimitive.List
            data-slot="command-list"
            className={cn('max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden', className)}
            {...props}
        />
    );
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
    return <CommandPrimitive.Empty data-slot="command-empty" className="py-6 text-center text-sm" {...props} />;
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
    return (
        <CommandPrimitive.Group
            data-slot="command-group"
            className={cn(
                'text-text-primary [&_[cmdk-group-heading]]:text-text-secondary overflow-hidden px-0 py-2 [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:leading-4',
                className,
            )}
            {...props}
        />
    );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
    return <CommandPrimitive.Separator data-slot="command-separator" className={cn('bg-border-subtle -mx-1 h-px', className)} {...props} />;
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
    return (
        <CommandPrimitive.Item
            data-slot="command-item"
            className={cn(
                "data-[selected=true]:bg-surface-secondary data-[selected=true]:text-text-primary [&_svg:not([class*='text-'])]:text-text-secondary outline-hidden relative flex cursor-default select-none items-center gap-2 rounded-sm px-4 py-2 text-sm data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className,
            )}
            {...props}
        />
    );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
    return <span data-slot="command-shortcut" className={cn('text-text-tertiary ml-auto text-xs tracking-widest', className)} {...props} />;
}

export { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut, CommandSeparator };
